/**
 * Digits-only input guards for the Reports filter range fields.
 *
 * The framework AC (112312) requires a numeric range field to "reject non-numeric characters as
 * they are typed and strip them from pasted input". antd's `InputNumber` does **not** do this: it
 * lets any text sit in the box while you type and only parses on blur, so `abc12x` is visible until
 * focus leaves. These two helpers are what the drawers wire onto the input to close that gap — the
 * same reason `<TextField>` ships its opt-in `transform` for digits-only strings.
 *
 * `allowDecimal` is per metric: Avg runtime/day is a division and carries decimals, while every
 * count (Total active time, Total alerts, No. of items, device counts) is whole.
 */

/** True when a keystroke may reach a numeric range field. */
export function isNumericKey(key: string, allowDecimal: boolean): boolean {
  // Editing and navigation keys report multi-character names (Backspace, ArrowLeft, Tab…) and must
  // always pass; only single printable characters are candidates for rejection.
  if (key.length > 1) return true;
  if (key >= '0' && key <= '9') return true;
  return allowDecimal && key === '.';
}

/** The numeric residue of a pasted string: digits, plus at most one decimal point when the metric
 *  carries decimals. A minus sign is dropped rather than honoured — every Reports range field is
 *  non-negative. */
export function stripToNumeric(text: string, allowDecimal: boolean): string {
  const digitsAndDots = text.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
  if (!allowDecimal) return digitsAndDots;
  // Keep the first '.' and drop the rest, so "1.2.3" pastes as "1.23" rather than becoming NaN.
  const firstDot = digitsAndDots.indexOf('.');
  if (firstDot === -1) return digitsAndDots;
  return (
    digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, '')
  );
}
